import type { IBackupRepository, IBackupQuotaAdapter, QuotaCheck } from '@app/domain/ports';
import type { QuotaUsage } from '@app/domain/entities';

/**
 * Backup quota adapter — pure TypeScript, framework-agnostic.
 *
 * Calculates local storage usage and enforces a configurable quota limit.
 * The quota limit (in bytes) is supplied via constructor callback so this class
 * has zero dependency on app-layer stores (AppStore.backupQuotaMB).
 */
export class BackupQuotaAdapter implements IBackupQuotaAdapter {
  constructor(
    private readonly backupRepository: IBackupRepository,
    /** Returns the configured quota ceiling in bytes — called on each usage check. */
    private readonly getQuotaLimitBytes: () => number,
  ) {}

  async calculateUsage(): Promise<QuotaUsage> {
    return this.calculateQuotaUsage();
  }

  async calculateQuotaUsage(): Promise<QuotaUsage> {
    const used = await this.calculateLocalBackupsTotalSize();
    const total = this.getQuotaLimitBytes();
    const percentage = (used / total) * 100;
    return { used, total, percentage };
  }

  async canStore(size: number): Promise<QuotaCheck> {
    const quota = await this.calculateQuotaUsage();
    return { canStore: quota.used + size <= quota.total };
  }

  private async calculateLocalBackupsTotalSize(): Promise<number> {
    const backups = await this.backupRepository.findAll();
    return backups.reduce((total, backup) => (backup.hasLocalData ? total + backup.size : total), 0);
  }
}
